import { useState, useEffect, useRef, useCallback } from "react";
import { X, MessageCircle, ChevronDown } from "lucide-react";
import { useAssistantSettings, ASSISTANT_CHARACTERS, AssistantCharacter } from "@/shared/hooks/useAssistantSettings";
import { cn } from "@/shared/lib/utils";

// ── Helpers ──────────────────────────────────────────────────────────────────

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ── Bubble ────────────────────────────────────────────────────────────────────

interface BubbleProps {
  character: AssistantCharacter;
  text: string;
  onClose: () => void;
  onClick: () => void;
}

const AssistantBubble = ({ character, text, onClose, onClick }: BubbleProps) => {
  return (
    <div
      className={cn(
        "flex items-end gap-2 animate-in slide-in-from-bottom-4 fade-in duration-300",
      )}
    >
      {/* Avatar */}
      <button
        onClick={onClick}
        className={cn(
          "relative shrink-0 w-12 h-12 rounded-full border-2 overflow-hidden shadow-lg",
          "hover:scale-105 transition-transform cursor-pointer",
          character.bubbleStyle.border,
        )}
        title={`${character.name} — ${character.role}`}
      >
        {character.avatar ? (
          <img src={character.avatar} alt={character.name} className="w-full h-full object-cover" />
        ) : (
          <div className={cn("w-full h-full flex items-center justify-center text-xl", character.bubbleStyle.bg)}>
            {character.emoji}
          </div>
        )}
        <span className="absolute -bottom-0.5 -right-0.5 text-xs leading-none">
          {character.emoji}
        </span>
      </button>

      {/* Balloon */}
      <div className="relative max-w-[220px]">
        <div
          className={cn(
            "rounded-2xl rounded-bl-none border px-3.5 py-2.5 shadow-md cursor-pointer",
            "hover:shadow-lg transition-shadow",
            character.bubbleStyle.bg,
            character.bubbleStyle.border,
          )}
          onClick={onClick}
        >
          {/* Name tag */}
          <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-1 opacity-70", character.bubbleStyle.text)}>
            {character.name} · {character.role}
          </p>
          {/* Message */}
          <p className={cn("text-sm font-medium leading-snug", character.bubbleStyle.text)}>
            {text}
          </p>
        </div>
        {/* Close */}
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className={cn(
            "absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center",
            "shadow border border-border bg-background text-muted-foreground",
            "hover:text-foreground hover:bg-muted transition-colors",
          )}
        >
          <X className="w-2.5 h-2.5" />
        </button>
      </div>
    </div>
  );
};

// ── Collapsed FAB ──────────────────────────────────────────────────────────────

interface FabProps {
  activeCharacters: AssistantCharacter[];
  onClick: () => void;
  hasNew: boolean;
}

const AssistantFab = ({ activeCharacters, onClick, hasNew }: FabProps) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-12 h-12 rounded-full shadow-lg border border-border bg-background",
        "flex items-center justify-center hover:scale-105 transition-transform",
        "relative",
      )}
      title="Assistentes virtuais"
    >
      <MessageCircle className="w-5 h-5 text-primary" />
      {hasNew && (
        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-primary border-2 border-background animate-pulse" />
      )}
      {activeCharacters.length > 0 && (
        <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-primary text-[9px] font-bold text-primary-foreground flex items-center justify-center border border-background">
          {activeCharacters.length}
        </span>
      )}
    </button>
  );
};

// ── Expanded Panel ─────────────────────────────────────────────────────────────

interface PanelProps {
  activeCharacters: AssistantCharacter[];
  onSelectCharacter: (c: AssistantCharacter) => void;
  onClose: () => void;
}

const AssistantPanel = ({ activeCharacters, onSelectCharacter, onClose }: PanelProps) => {
  return (
    <div className={cn(
      "bg-background border border-border rounded-2xl shadow-xl p-3 w-64",
      "animate-in slide-in-from-bottom-4 fade-in duration-300",
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/50">
        <p className="text-xs font-semibold text-foreground font-display">Equipe de Assistentes</p>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
      {/* Character list */}
      <div className="space-y-1.5">
        {activeCharacters.map((c) => (
          <button
            key={c.id}
            onClick={() => onSelectCharacter(c)}
            className={cn(
              "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left",
              "hover:bg-muted/60 transition-colors group",
            )}
          >
            <span className="text-lg leading-none shrink-0">{c.emoji}</span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold truncate text-foreground">{c.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">{c.role}</p>
            </div>
            <MessageCircle className={cn(
              "w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity",
              c.bubbleStyle.text,
            )} />
          </button>
        ))}
      </div>
      {activeCharacters.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-3">
          Nenhum assistente ativo. Ative-os nas Configurações.
        </p>
      )}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const IDLE_INTERVAL_MS = 45_000; // 45 seconds between idle pops
const GREETING_DELAY_MS = 2_000; // initial greeting after mount

export const FloatingAssistant = () => {
  const { settings } = useAssistantSettings();
  const [bubble, setBubble] = useState<{ character: AssistantCharacter; text: string } | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const greetingShown = useRef(false);

  const activeCharacters = ASSISTANT_CHARACTERS.filter(
    (c) => settings.enabled && settings.characters[c.id],
  );

  const showBubble = useCallback((character: AssistantCharacter, phrase: string) => {
    setBubble({ character, text: phrase });
    setHasNew(true);
    setPanelOpen(false);
  }, []);

  const triggerIdle = useCallback(() => {
    if (activeCharacters.length === 0) return;
    const c = pickRandom(activeCharacters);
    showBubble(c, pickRandom(c.idlePhrases));
  }, [activeCharacters, showBubble]);

  // Initial greeting
  useEffect(() => {
    if (activeCharacters.length === 0 || greetingShown.current) return;
    const t = setTimeout(() => {
      if (greetingShown.current) return;
      greetingShown.current = true;
      const c = pickRandom(activeCharacters);
      showBubble(c, pickRandom(c.greetings));
    }, GREETING_DELAY_MS);
    return () => clearTimeout(t);
  }, [activeCharacters.length]);  

  // Idle bubbles
  useEffect(() => {
    if (activeCharacters.length === 0) return;
    idleTimer.current = setTimeout(function repeat() {
      triggerIdle();
      idleTimer.current = setTimeout(repeat, IDLE_INTERVAL_MS);
    }, IDLE_INTERVAL_MS);
    return () => { if (idleTimer.current) clearTimeout(idleTimer.current); };
  }, [activeCharacters.length, triggerIdle]);

  const handleSelectCharacter = (c: AssistantCharacter) => {
    showBubble(c, pickRandom(c.greetings));
  };

  const closeBubble = () => {
    setBubble(null);
    setHasNew(false);
  };

  if (!settings.enabled || activeCharacters.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Active bubble */}
      {bubble && !panelOpen && (
        <AssistantBubble
          character={bubble.character}
          text={bubble.text}
          onClose={closeBubble}
          onClick={() => { closeBubble(); setPanelOpen(true); }}
        />
      )}

      {/* Expanded panel */}
      {panelOpen && (
        <AssistantPanel
          activeCharacters={activeCharacters}
          onSelectCharacter={handleSelectCharacter}
          onClose={() => setPanelOpen(false)}
        />
      )}

      {/* FAB */}
      <AssistantFab
        activeCharacters={activeCharacters}
        onClick={() => {
          if (panelOpen) {
            setPanelOpen(false);
          } else {
            closeBubble();
            setPanelOpen(true);
          }
        }}
        hasNew={hasNew && !panelOpen}
      />
    </div>
  );
};

export default FloatingAssistant;
